/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-extra-boolean-cast */
import { Injectable, inject } from '@angular/core';
import { arrayAdd, arrayRemove, arrayUpdate, arrayUpsert, EntityStore, StoreConfig } from '@datorama/akita';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import { EmploymentIncomeExpenseModel, IncomeModel, EmploymentModel } from '../../cmg-1003/models';
import { EmploymentStatus } from '../../cmg-1003/models/lookups/employment.lookups';
import { SessionStore } from '../../core/state';
import { EmploymentIncomeExpensesUtils } from './employment-income-expenses.utils';
import { iStoreActions } from '../core/store-actions.interface';

function createInitialState(): EmploymentIncomeExpenseModel {
  return <EmploymentIncomeExpenseModel>{};
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'cmg-1003-employment-income-expenses-store', idKey: 'applicationId' })
export class Cmg1003EmploymentIncomeExpensesStore
  extends EntityStore<EmploymentIncomeExpenseModel>
  implements iStoreActions<EmploymentIncomeExpenseModel>
{
  private readonly _sessionStore = inject(SessionStore);

  private _initValue: EmploymentIncomeExpenseModel[] = [];
  constructor() {
    super(createInitialState());
  }

  /**
   * Processes the employment Income expenses model for the UI.
   * Changes made in this function will not set the dirty flag.
   * @param empIncExpensesModel
   */
  override akitaPreAddEntity(empIncExpensesModel: EmploymentIncomeExpenseModel): EmploymentIncomeExpenseModel {
    //
    [...(empIncExpensesModel.borrowerEmployers || []), ...(empIncExpensesModel.coBorrowerEmployers || [])].forEach((employment) => {
      // add empty employment income row for each employer, if no income is defined.
      if (
        !empIncExpensesModel.grossMonthlyIncomes.some(
          (inc) => inc.employmentId === employment.employmentId && inc.borrowerId === employment.borrowerId,
        )
      ) {
        empIncExpensesModel.grossMonthlyIncomes.push(IncomeModel.createEmpty(employment.borrowerId, employment.employmentId, 1 /* Base Income */));
      }

      const propertyInsurance = empIncExpensesModel.combinedHousingExpenses?.propertyInsurance;
      if (propertyInsurance?.proposedAmount === undefined) {
        // This is so that wherever we call form.patchValue with the combinedHousingExpenses object, this field will be cleared out correctly.
        propertyInsurance.proposedAmount = null;
      }
    });

    this._initValue.push(JSON.parse(JSON.stringify(empIncExpensesModel)));
    return empIncExpensesModel;
  }

  override akitaPreUpdateEntity(_: Readonly<EmploymentIncomeExpenseModel>, nextEntity: any): EmploymentIncomeExpenseModel {
    this._sessionStore.update({ lastUpdatedDateTime: Date.now() });
    return nextEntity;
  }

  /**
   * Updates
   * b/c of byte an empty default(primary) employment is added, however that default employment does
   * not exist when adding co-borrower or new application. The convoluted logic here accounts for that.
   * @param appId
   * @param isBorrower
   * @param updatedEmployment
   */
  updateEmployment(appId: number, isBorrower: boolean, updatedEmployment: EmploymentModel) {
    // TODO: temporary leaving it here till I understand what this really does and where it should be calculated.
    const yearsMonths = EmploymentIncomeExpensesUtils.calculateNoOfYearsAndMonths(
      updatedEmployment.dateFrom,
      updatedEmployment.dateTo,
      updatedEmployment.employmentType,
    );
    updatedEmployment.yearsInJob = yearsMonths.noOfYears;
    updatedEmployment.monthsInJob = yearsMonths.noOfMonths;

    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      const updatedModel = Object.assign({}, empIncExpModel);
      if (updatedEmployment.employmentId === 0) {
        if (updatedEmployment.employmentType === EmploymentStatus.Primary) {
          if (isBorrower) {
            updatedEmployment.employmentId = updatedModel.borrowerDefaultEmploymentId =
              updatedModel.borrowerDefaultEmploymentId ?? -CmgUiUtilities.getUniqueNumber();
          } else {
            updatedEmployment.employmentId = updatedModel.coBorrowerDefaultEmploymentId =
              updatedModel.coBorrowerDefaultEmploymentId ?? -CmgUiUtilities.getUniqueNumber();
          }
        } else {
          updatedEmployment.employmentId = -CmgUiUtilities.getUniqueNumber();
        }
      }
      if (isBorrower) {
        return {
          ...updatedModel,
          borrowerEmployers: arrayUpsert(updatedModel.borrowerEmployers || [], updatedEmployment.employmentId, updatedEmployment, 'employmentId'),
        };
      } else {
        return {
          ...updatedModel,
          coBorrowerEmployers: arrayUpsert(updatedModel.coBorrowerEmployers || [], updatedEmployment.employmentId, updatedEmployment, 'employmentId'),
        };
      }
    });
  }

  deleteEmployment(appId: number, isBorrower: boolean, employmentId: number) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      if (isBorrower) {
        return {
          ...empIncExpModel,
          borrowerEmployers:
            employmentId < 0
              ? arrayRemove(empIncExpModel.borrowerEmployers, employmentId, 'employmentId')
              : arrayUpdate(empIncExpModel.borrowerEmployers, employmentId, { isDeleted: true }, 'employmentId'),
          grossMonthlyIncomes:
            employmentId < 0
              ? arrayRemove(empIncExpModel.grossMonthlyIncomes, employmentId, 'employmentId')
              : arrayUpdate(empIncExpModel.grossMonthlyIncomes, employmentId, { isDeleted: true }, 'employmentId'),
        };
      } else {
        return {
          ...empIncExpModel,
          coBorrowerEmployers:
            employmentId < 0
              ? arrayRemove(empIncExpModel.coBorrowerEmployers, employmentId, 'employmentId')
              : arrayUpdate(empIncExpModel.coBorrowerEmployers, employmentId, { isDeleted: true }, 'employmentId'),
          grossMonthlyIncomes:
            employmentId < 0
              ? arrayRemove(empIncExpModel.grossMonthlyIncomes, employmentId, 'employmentId')
              : arrayUpdate(empIncExpModel.grossMonthlyIncomes, employmentId, { isDeleted: true }, 'employmentId'),
        };
      }
    });
  }

  deleteBorrowerPrimaryEmployment(appId: number, isBorrower: boolean, employmentId: number) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      if (isBorrower) {
        return {
          ...empIncExpModel,
          grossMonthlyIncomes: arrayUpdate(empIncExpModel.grossMonthlyIncomes, employmentId, { isDeleted: true }, 'employmentId'),
          borrowerEmployers: arrayUpdate(empIncExpModel.borrowerEmployers, employmentId, { isDeleted: true }, 'employmentId'),
        };
      } else {
        return {
          ...empIncExpModel,
          grossMonthlyIncomes: arrayUpdate(empIncExpModel.grossMonthlyIncomes, employmentId, { isDeleted: true }, 'employmentId'),
          coBorrowerEmployers: arrayUpdate(empIncExpModel.coBorrowerEmployers, employmentId, { isDeleted: true }, 'employmentId'),
        };
      }
    });
  }

  addIncome(appId: number, incomeModel: IncomeModel) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      return {
        ...empIncExpModel,
        grossMonthlyIncomes: [...empIncExpModel.grossMonthlyIncomes, incomeModel],
      };
    });
  }

  deleteIncome(appId: number, incomeId: number, keyName = 'incomeId') {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      return {
        ...empIncExpModel,
        grossMonthlyIncomes:
          incomeId < 0
            ? arrayRemove(empIncExpModel.grossMonthlyIncomes, incomeId, keyName)
            : arrayUpdate(empIncExpModel.grossMonthlyIncomes, incomeId, { isDeleted: true }, keyName),
      };
    });
  }

  updateIncomeAmount(appId: number, incomeId: number, amount: number) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      return {
        ...empIncExpModel,
        grossMonthlyIncomes: arrayUpdate(empIncExpModel.grossMonthlyIncomes, incomeId, { amount: amount }, 'incomeId'),
      };
    });
  }

  /**
   * Consolidate all upsert for Income
   * @param appId
   * @param incomeId
   * @param incomeType
   * @param amount
   * @param borrowerId - only for new Income
   * @param employmentId - only for new Income
   */
  updateIncome(appId: number, incomeId: number, incomeType: number, amount: number, incomeRate: number, borrowerId: number, employmentId: number) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      if (empIncExpModel.grossMonthlyIncomes.some((inc) => inc.incomeId === incomeId)) {
        return {
          ...empIncExpModel,
          grossMonthlyIncomes: arrayUpdate(
            empIncExpModel.grossMonthlyIncomes,
            incomeId,
            { amount: amount, incomeRate: incomeRate, incomeType: incomeType },
            'incomeId',
          ),
        };
      } else {
        const newIncomeModel = IncomeModel.createEmpty(borrowerId, employmentId, incomeType);
        newIncomeModel.incomeId = incomeId;
        newIncomeModel.amount = amount;
        newIncomeModel.incomeRate = incomeRate;
        return {
          ...empIncExpModel,
          grossMonthlyIncomes: arrayAdd(empIncExpModel.grossMonthlyIncomes, newIncomeModel),
        };
      }
    });
  }

  updateIncomeModel(appId: number, incomeId: number, income: IncomeModel) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      return {
        ...empIncExpModel,
        grossMonthlyIncomes: arrayUpdate(empIncExpModel.grossMonthlyIncomes, incomeId, income, 'incomeId'),
      };
    });
  }

  addUpdateIncome(appId: number, incomeId: number, incomeModel: IncomeModel) {
    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      if (empIncExpModel.grossMonthlyIncomes.some((inc) => inc.incomeId === incomeId)) {
        return {
          ...empIncExpModel,
          grossMonthlyIncomes: arrayUpdate(empIncExpModel.grossMonthlyIncomes, incomeId, incomeModel, 'incomeId'),
        };
      } else {
        return {
          ...empIncExpModel,
          grossMonthlyIncomes: [...empIncExpModel.grossMonthlyIncomes, incomeModel],
        };
      }
    });
  }

  updateEmploymentWithDefault(appId: number, isBorrower: boolean, defaultEmploymentId: number, updatedEmployment: EmploymentModel) {
    const yearsMonths = EmploymentIncomeExpensesUtils.calculateNoOfYearsAndMonths(
      updatedEmployment.dateFrom,
      updatedEmployment.dateTo,
      updatedEmployment.employmentType,
    );
    updatedEmployment.yearsInJob = yearsMonths.noOfYears;
    updatedEmployment.monthsInJob = yearsMonths.noOfMonths;

    this.update(appId, (empIncExpModel: EmploymentIncomeExpenseModel) => {
      const updatedModel = Object.assign({}, empIncExpModel);
      if (isBorrower) {
        if (updatedEmployment.employmentType === EmploymentStatus.Primary && defaultEmploymentId > 0) {
          updatedModel.borrowerDefaultEmploymentId = defaultEmploymentId;
        }
        return {
          ...updatedModel,
          borrowerEmployers: arrayUpsert(updatedModel.borrowerEmployers || [], updatedEmployment.employmentId, updatedEmployment, 'employmentId'),
        };
      } else {
        if (updatedEmployment.employmentType === EmploymentStatus.Primary && defaultEmploymentId > 0) {
          updatedModel.coBorrowerDefaultEmploymentId = defaultEmploymentId;
        }
        return {
          ...updatedModel,
          coBorrowerEmployers: arrayUpsert(updatedModel.coBorrowerEmployers || [], updatedEmployment.employmentId, updatedEmployment, 'employmentId'),
        };
      }
    });
  }

  get initialValue() {
    return this._initValue;
  }
}
